import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useShipmentDetailSearch } from "../../../hooks/react-query/preplanning/useShipmentDetails";
import { usePlannedRoutes } from "../../../hooks/react-query/usePlannedRoutes";
import { useDateSearchParamOrFallbackToToday } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import { PlanSummary } from "../../../types/planning/plan.type";
import { dateToInt } from "../../../utils/dateTimeHelper";

import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { useOSADShipments } from "../../../hooks/react-query/operations/useOSAD";
import { useAutoSequenceRoutes } from "../../../hooks/react-query/useAutoSequenceRoute";
import { useDeliveryRoutesSequenceStatus } from "../../../hooks/react-query/useDeliveryRoutesSequenceStatus";
import { useUnassignedShipmentIds } from "../../../hooks/react-query/useUnassignedShipments";
import { osadDefaultValue } from "../../../mocks/operations/osad.mock";
import { Shipment } from "../../../services/prePlanningService.types";
import type { OSADShipments } from "../../../types/operations/osad.type";
import { onAutoSequenceRoutesFinished } from "./utils";

type InboundPlanningContextValue = {
  plannedRoutes: PlanSummary[];
  shipments: Shipment[];
  osad: OSADShipments;
  isLoading: boolean;
  refetchPlans: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<PlanSummary[], Error>>;
  refetchShipments: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<Shipment[], Error>>;
  setAutoSequenceTimestamp: Dispatch<React.SetStateAction<number>>;
  /** Route IDs for the in-flight auto-sequence request (overlaps one frame before plannedRoutes shows "processing"). */
  pendingAutoSequenceRouteIds: number[];
  setPendingAutoSequenceRouteIds: Dispatch<SetStateAction<number[]>>;
};

type InboundPlanningProviderType = {
  children: ReactNode;
};

const InboundPlanningContext =
  createContext<InboundPlanningContextValue | null>(null);

export const useInboundPlanningContext = (): InboundPlanningContextValue => {
  const context = useContext(InboundPlanningContext);
  if (!context) {
    throw new Error(
      "useInboundPlanningContext must be used within a InboundPlanningProvider"
    );
  }
  return context;
};

export const InboundPlanningProvider = ({
  children,
}: InboundPlanningProviderType) => {
  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const planDate = dateToInt(selectedDate);
  const [autoSequenceTimestamp, setAutoSequenceTimestamp] = useState(0);
  const [pendingAutoSequenceRouteIds, setPendingAutoSequenceRouteIds] =
    useState<number[]>([]);

  const isOsadFeatureEnabled = useIsFeatureFlagEnabled(
    "inbound-5931-OSAD-assign-form-client"
  );

  const {
    data: unassignedShipmentIds = [],
    isLoading: isUnassignedShipmentsLoading,
  } = useUnassignedShipmentIds(serviceCenter.id, planDate);

  const {
    data: plannedRoutes = [],
    isLoading: isPlannedRoutesLoading,
    refetch: refetchPlans,
  } = usePlannedRoutes(planDate, serviceCenter.id);

  const deliveryRouteIdsForSequencePolling = useMemo(() => {
    const fromPlans = plannedRoutes
      .filter(
        (route) =>
          route.type === "delivery_route" &&
          route.sequenceStatus === "processing"
      )
      .map((route) => route.id);
    const merged = [...new Set([...fromPlans, ...pendingAutoSequenceRouteIds])];
    merged.sort((a, b) => a - b);
    return merged;
  }, [plannedRoutes, pendingAutoSequenceRouteIds]);

  const {
    data: shipments = [],
    isLoading: isShipmentsDetailsLoading,
    refetch: refetchShipments,
  } = useShipmentDetailSearch({
    search: {
      shipmentIds: [
        ...new Set(plannedRoutes.flatMap(({ shipmentIds }) => shipmentIds)),
        ...unassignedShipmentIds.map(Number),
      ],
      sicId: serviceCenter.id,
      planDate,
      addPlanningInfo: true,
    },
    enabled: !isUnassignedShipmentsLoading,
    onlyIncludeHighlightedRows: true,
  });

  const { data: osad = osadDefaultValue, isLoading: isLoadingOsadShipments } =
    useOSADShipments(serviceCenter.id, isOsadFeatureEnabled);

  const { mutateAsync: autoSequenceRoutes, isPending: isAutoSequencing } =
    useAutoSequenceRoutes();

  useDeliveryRoutesSequenceStatus({
    routeIds: deliveryRouteIdsForSequencePolling,
    timestamp: autoSequenceTimestamp,
    onAutoSequenceRoutesFinished: async (result) => {
      // Refetch the latest data from backend
      await refetchPlans();
      await refetchShipments();

      // Reset timestamp to 0 for next operation
      setAutoSequenceTimestamp(0);

      // Show toast notification
      onAutoSequenceRoutesFinished({
        planDate: dateToInt(selectedDate),
        sicId: serviceCenter.id,
        sequenceResult: result,
        retryMutation: autoSequenceRoutes,
      });
    },
  });

  const isLoading =
    isAutoSequencing ||
    isUnassignedShipmentsLoading ||
    isPlannedRoutesLoading ||
    isShipmentsDetailsLoading ||
    isLoadingOsadShipments;

  const inboundPlanningContextValue = useMemo(
    () => ({
      plannedRoutes,
      shipments,
      osad,
      isLoading,
      refetchPlans,
      refetchShipments,
      setAutoSequenceTimestamp,
      pendingAutoSequenceRouteIds,
      setPendingAutoSequenceRouteIds,
    }),
    [
      plannedRoutes,
      shipments,
      osad,
      isLoading,
      refetchPlans,
      refetchShipments,
      pendingAutoSequenceRouteIds,
    ]
  );

  return (
    <InboundPlanningContext.Provider value={inboundPlanningContextValue}>
      {children}
    </InboundPlanningContext.Provider>
  );
};
